import React, { useState, useContext, useEffect } from "react";
import DotContext from "../contexts/DotContext";
import { SessionThemeWrapper } from "../contexts/SessionThemeContext";
import DynamicTitle from "./DynamicTitle";
import Sidebar from "./Sidebar";
import DotMovement from "./DotMovement";
import MoodTracker from "./MoodTracker";
import { markTourCompleted, markTourSeenOnServer } from "../utils/tourUtils";
import { trackEvent } from "../utils/posthog";

function TourSession() {
  const [currentStep, setCurrentStep] = useState(0);
  const [showMoodTracker, setShowMoodTracker] = useState(false);
  const [showDotMovement, setShowDotMovement] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const {
    bgColor,
    dotColor,
    dotGlow,
    dotBrightness,
    dotSize,
    dotSpeed,
    setSidebarVisible: setContextSidebarVisible,
  } = useContext(DotContext);

  useEffect(() => {
    trackEvent("tour_started");
  }, []);

  const tourSteps = [
    {
      id: 'welcome',
      title: 'Welcome to RealignMind!',
      content: 'Let\'s take a quick tour to show you how our sessions work. This will help you feel comfortable before your first real session.',
      position: 'center',
      showOverlay: true,
      highlight: null,
      action: () => {
        // Clean slate for tour start
        setShowMoodTracker(false);
        setShowDotMovement(false);
        setSidebarVisible(false);
        setContextSidebarVisible(false);
      }
    },
    {
      id: 'mood-tracking',
      title: 'Mood Tracking',
      content: 'Before each session, we\'ll ask you to rate your current mood on a scale of 1-10. This helps us track your progress over time. You can use the slider or click the numbers directly. You can also add notes about how you\'re feeling. The mood modal appears before and after each session to help you see patterns and progress over time.',
      position: 'center',
      showOverlay: true,
      highlight: null,
      action: () => {
        setShowMoodTracker(true);
        // Don't hide the mood tracker during this step
      }
    },
    {
      id: 'sidebar-controls',
      title: 'Customization Controls',
      content: 'The sidebar lets you customize your session experience. Switch between dark and light themes, adjust dot brightness, size, and speed to make it comfortable for you.',
      position: 'left',
      showOverlay: true,
      highlight: 'sidebar',
      action: () => {
        setSidebarVisible(true);
        setContextSidebarVisible(true);
        setShowMoodTracker(false); // Hide mood tracker during sidebar demo
      }
    },
    {
      id: 'settings-explanation',
      title: 'Understanding the Settings',
      content: 'Here\'s what each setting does: Theme - Dark, light, or follow your system. Dot Brightness - Dim the dot if it feels too intense. Dot Size - Make it larger if you have trouble seeing it. Speed - Slower speeds (0.4 Hz) are gentler, faster speeds (2.0 Hz) are more intense.',
      position: 'left',
      showOverlay: true,
      highlight: 'sidebar',
      action: () => {
        // Keep sidebar visible for this step
        setShowMoodTracker(false); // Hide mood tracker during settings explanation
      }
    },
    {
      id: 'dot-movement',
      title: 'Bilateral Stimulation Session',
      content: 'This is the core of your session. The dot moves back and forth while you follow it with your eyes. The most important piece is that you keep following the dot during the session - this bilateral stimulation is what helps you settle and find calm.',
      position: 'center',
      showOverlay: true,
      highlight: 'dot',
      action: () => {
        setShowDotMovement(true);
        setSidebarVisible(false);
        setContextSidebarVisible(false);
        setShowMoodTracker(false); // Hide mood tracker during dot demo
      }
    },
    {
      id: 'following-dot-importance',
      title: 'The Key to Success',
      content: 'Remember: The most important part of your session is keeping your eyes focused on the moving dot. This bilateral stimulation (left-right movement) is what helps you settle and clear your head. Don\'t worry if your mind wanders - just gently bring your attention back to following the dot.',
      position: 'center',
      showOverlay: true,
      highlight: 'dot',
      action: () => {
        // Keep dot movement visible for this step
        setShowMoodTracker(false); // Hide mood tracker during dot importance explanation
      }
    },
    {
      id: 'session-complete',
      title: 'Session Complete!',
      content: 'After your session, we\'ll ask for your mood again to track any changes. You can also add notes about your experience. This helps you see your progress over time.',
      position: 'center',
      showOverlay: true,
      highlight: null,
      action: () => {
        setShowDotMovement(false);
        setShowMoodTracker(true);
      }
    },
    {
      id: 'ready-to-start',
      title: 'You\'re Ready!',
      content: 'Now you know how everything works! You can start with either a guided session (with a script) or an unguided session (free exploration). Remember: keep following the dot with your eyes during the session.',
      position: 'center',
      showOverlay: true,
      highlight: null,
      action: () => {
        // Clean finish - hide all demo elements
        setShowMoodTracker(false);
        setShowDotMovement(false);
        setSidebarVisible(false);
        setContextSidebarVisible(false);
      }
    }
  ];

  const handleNext = async () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (tourSteps[nextStep].action) {
        tourSteps[nextStep].action();
      }
    } else {
      markTourCompleted();
      trackEvent("tour_completed");
      await markTourSeenOnServer();
      window.location.href = '/sessions';
    }
  };

  const handleSkip = async () => {
    markTourCompleted();
    trackEvent("tour_skipped", { skipped_at_step: currentStep + 1 });
    await markTourSeenOnServer();
    window.location.href = '/sessions';
  };

  const currentStepData = tourSteps[currentStep];

  return (
    <SessionThemeWrapper className="flex flex-col min-h-screen bg-session-bg text-session-text">
      <DynamicTitle>Tour</DynamicTitle>
      
      {/* Main Session Area */}
      <div className="flex-grow relative">
        {/* Background with current settings */}
        <div 
          className="w-full h-full flex items-center justify-center relative"
          style={{ backgroundColor: bgColor }}
        >
          {/* Sidebar */}
          {sidebarVisible && (
            <div className="absolute left-0 top-0 h-full z-20">
              <Sidebar />
            </div>
          )}

          {/* Dot Movement */}
          {showDotMovement && (
            <div className="w-full h-full flex items-center justify-center relative">
              <button
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-session-accent text-session-bg py-2 px-6 rounded hover:opacity-90 transition duration-200 z-50 font-bold"
                onClick={() => setIsPaused(!isPaused)}
              >
                {isPaused ? "Resume" : "Pause"}
              </button>
              <DotMovement
                shouldRun={showDotMovement}
                isPaused={isPaused}
                bgColor={bgColor}
                dotColor={dotColor}
                dotGlow={dotGlow}
                dotBrightness={dotBrightness}
                dotSize={dotSize}
                dotSpeed={dotSpeed}
                onCompletion={() => setShowDotMovement(false)}
                soundFile="Synth_Sine_C_hi.wav"
                mute={true}
              />
            </div>
          )}

          {/* Highlight Overlays */}
          {currentStepData.highlight === 'sidebar' && sidebarVisible && (
            <div className="absolute left-0 top-0 w-80 h-full bg-accent bg-opacity-20 border-4 border-accent rounded-lg z-10"></div>
          )}
          
          {currentStepData.highlight === 'dot' && showDotMovement && (
            <div className="absolute inset-0 bg-accent bg-opacity-20 border-4 border-accent rounded-lg z-10"></div>
          )}
        </div>

        {/* Tour Overlay */}
        {currentStepData.showOverlay && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md mx-4 p-6 relative">
              {/* Progress Indicator */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Step {currentStep + 1} of {tourSteps.length}</span>
                  <span>{Math.round(((currentStep + 1) / tourSteps.length) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-accent h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {currentStepData.title}
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {currentStepData.content}
              </p>

              {/* Step-specific tips */}
              {currentStepData.id === 'mood-tracking' && (
                <div className="bg-background border-l-4 border-accent p-4 mb-6 rounded">
                  <p className="text-text text-sm font-medium">
                    💡 <strong>Try it out:</strong> The mood tracker is shown below. 
                    You can interact with it to see how it works, then click "Next" to continue the tour.
                  </p>
                </div>
              )}
              
              {currentStepData.id === 'following-dot-importance' && (
                <div className="bg-background border-l-4 border-accent p-4 mb-6 rounded">
                  <p className="text-text text-sm font-medium">
                    💡 <strong>Pro Tip:</strong> It's normal for your mind to wander during sessions. 
                    When you notice this, simply return your attention to following the dot.
                  </p>
                </div>
              )}
              
              {currentStepData.id === 'settings-explanation' && (
                <div className="bg-background border-l-4 border-secondary p-4 mb-6 rounded">
                  <p className="text-text text-sm font-medium">
                    🎨 <strong>Remember:</strong> You can change these settings anytime during your session. 
                    Find what feels comfortable for you!
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={handleSkip}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Skip Tour
                </button>
                <div className="flex gap-3">
                  {currentStep > 0 && (
                    <button
                      onClick={() => setCurrentStep(currentStep - 1)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleNext}
                    className={`px-6 py-2 rounded-md transition-colors font-semibold ${
                      currentStep === tourSteps.length - 1 
                        ? 'bg-accent text-white hover:bg-primary text-lg px-8 py-3' 
                        : 'bg-accent text-white hover:bg-primary'
                    }`}
                  >
                    {currentStep === tourSteps.length - 1 ? '🎉 Start Your Sessions!' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mood Tracker Modal */}
      {showMoodTracker && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-xl max-w-md mx-4 p-6 relative">
            <MoodTracker
              inline
              onSubmit={() => {}}
            />
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 mb-3">
                This is how the mood tracker works. Try adjusting the slider or clicking numbers.
              </p>
              <button
                onClick={() => {
                  setShowMoodTracker(false);
                  handleNext();
                }}
                className="px-4 py-2 bg-accent text-white rounded-md hover:bg-primary transition-colors text-sm font-medium"
              >
                Continue Tour
              </button>
            </div>
          </div>
        </div>
      )}
    </SessionThemeWrapper>
  );
}

export default TourSession;
